import { fetchAuthSession } from 'aws-amplify/auth';
import type { VendorAppStatus, VendorStatus } from './vendorChips';

/**
 * Thin client for the admin-api (glplus-admin-api). Attaches the Cognito id
 * token as a Bearer credential; the admin-api re-verifies it + checks the
 * caller's capability server-side. Self-contained (no shared-libs coupling) so
 * the portal can ship independently of the search-API config plumbing.
 */
const BASE = import.meta.env.VITE_ADMIN_API_URL as string | undefined;

export class AdminApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** A stable reason some routes send with a refusal. */
    public readonly code?: string,
    /** The rest of the refusal's body, when the route sends more than a message. */
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!BASE) throw new AdminApiError('VITE_ADMIN_API_URL is not configured', 0);
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const body = (payload as ({ error?: string; code?: string } & Record<string, unknown>) | null) ?? {};
    throw new AdminApiError(body.error ?? `Request failed (${res.status})`, res.status, body.code, body);
  }
  return (payload as { data: T }).data;
}

export const adminApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

// ─── Typed endpoints ─────────────────────────────────────────────────────────

/** The four apps a staff member can be scoped to, and the per-app access levels. */
export type AppName = 'consumer' | 'vendor' | 'driver' | 'admin';
export type AccessLevel = 'none' | 'view' | 'manage' | 'admin';
export type AppLevels = Record<AppName, AccessLevel>;
export const APP_NAMES: AppName[] = ['consumer', 'vendor', 'driver', 'admin'];
export const ACCESS_LEVELS: AccessLevel[] = ['none', 'view', 'manage', 'admin'];

export interface MeCapabilities {
  sub: string;
  email: string;
  /** 'admin' for any staff actor (derived from access, not token claims). */
  role: 'admin';
  groups: string[];
  /** Per-app access levels resolved from the staffer's tblglpemployees row. */
  levels: AppLevels;
  capabilities: string[];
}

// ─── Vendor applications (review console) ────────────────────────────────────

export interface VendorBusinessInfo {
  businessName?: string;
  businessType?: string;
  businessTypeOther?: string;
  contactName?: string;
  role?: string;
  contactPhone?: string;
  contactEmail?: string;
  website?: string;
  numberOfLocations?: string;
  address?: { line1?: string; line2?: string; city?: string; state?: string; postalCode?: string };
  snapAccepted?: boolean;
  fnsNumber?: string;
  approxProductCount?: string;
  interests?: string[];
  interestsOther?: string;
  [k: string]: unknown;
}

export interface VendorApplicationItem {
  applicationId: string;
  userId: string | null;
  vendorUserId: string | null;
  applicantEmail: string | null;
  applicantFirstName: string | null;
  applicantLastName: string | null;
  applicantPhoneE164: string | null;
  vendorId: string | null;
  businessInfo: VendorBusinessInfo;
  hours: Record<string, unknown> | null;
  posSystem: string | null;
  categories: string[] | null;
  motivation: string | null;
  dataShapeAssigned: string | null;
  staffVisitScheduledAt: string | null;
  adapterBuiltAt: string | null;
  vendorConfirmedAt: string | null;
  status: VendorAppStatus;
  reviewerUserId: string | null;
  reviewerDecisionAt: string | null;
  declinedAt: string | null;
  declinedReason: string | null;
  withdrawnAt: string | null;
  reapplyUnlockedAt: string | null;
  reapplyUnlockedBySub: string | null;
  duplicateBusinessWarning: {
    matchCount: number;
    matches: {
      applicationId: string;
      applicantEmail: string | null;
      matchedOn: ('address' | 'phone')[];
    }[];
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface VendorApplicant {
  glpUserId: string;
  email: string;
  displayName: string | null;
  status: string;
}

export interface VendorApplicationDetail extends VendorApplicationItem {
  applicant: VendorApplicant | null;
}

export type VendorAppFilter = VendorAppStatus | 'all' | 'open';

export interface VendorAppListFilters {
  status?: VendorAppFilter;
  q?: string;
  before?: string;
  limit?: number;
}

export type VendorTransitionAction =
  | 'start_review'
  | 'assign_data_shape'
  | 'schedule_visit'
  | 'skip_visit'
  | 'mark_adapter_built'
  | 'confirm_vendor'
  | 'decline';

export interface VendorTransitionBody {
  action: VendorTransitionAction;
  dataShape?: string;
  visitAt?: string;
  reason?: string;
}

export interface VendorAppListResult {
  status: string;
  count: number;
  items: VendorApplicationItem[];
  nextCursor: string | null;
  counts: Record<string, number>;
}

// ─── Vendor review queue (locations · products to identify · change requests) ──

export interface VendorRef {
  vendorId: string;
  businessName: string;
  glpVendorId: string | null;
}

/** What a vendor typed for a location, held until we approve it. */
export interface VendorLocationSubmitted {
  name: string;
  formattedAddress: string;
  phoneE164: string | null;
  website: string | null;
  openingHours: Record<string, string> | null;
  pickupSupported: boolean;
  deliverySupported: boolean;
  temporarilyClosed: boolean;
}

export type VendorLocationStatus = 'verifying' | 'live' | 'needs_changes';

export interface VendorLocationItem {
  storeId: string;
  vendorId: string | null;
  /** What shoppers see today — unchanged until the location is approved. */
  name: string;
  formattedAddress: string | null;
  phoneInternational: string | null;
  website: string | null;
  fnsNumber: string | null;
  fnsVerification: string | null;
  snapAccepted: boolean;
  vendorStatus: VendorLocationStatus | null;
  vendorStatusNote: string | null;
  vendorSource: string | null;
  vendorSubmittedAt: string | null;
  vendor: { businessName: string; glpVendorId: string | null; businessType: string | null } | null;
  submitted: VendorLocationSubmitted | null;
  match: {
    /** existing_store = a store shoppers already have · poi = a finder entry · none = new. */
    kind: 'existing_store' | 'poi' | 'none' | null;
    distanceM: number | null;
    poi: { name: string; address: string } | null;
    savedByShoppers: number;
  };
}

export interface VendorLocationListResult {
  counts: Partial<Record<VendorLocationStatus, number>>;
  items: VendorLocationItem[];
}

export type VendorCertificationStatus = 'proof_submitted' | 'in_review' | 'verified' | 'rejected' | 'expired';

export interface VendorCertificationReviewItem {
  certificationId: string;
  vendorId: string;
  storeId: string;
  certificationKey: string;
  customLabel: string | null;
  issuerName: string;
  certificateRef: string | null;
  issuedOn: string | null;
  expiresOn: string;
  status: VendorCertificationStatus;
  proofFileName: string;
  proofContentType: string;
  proofByteSize: number;
  proofSha256: string;
  submittedAt: string;
  reviewedBySub: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  archivedAt: string | null;
  vendor: { businessName: string; glpVendorId: string | null };
  store: { name: string; formattedAddress: string | null };
  submittedBy: { email: string; givenName: string | null; familyName: string | null } | null;
}

export interface VendorCertificationListResult {
  status: string;
  count: number;
  items: VendorCertificationReviewItem[];
  nextCursor: string | null;
}

export interface VendorCertificationEvidence {
  reviewUrl: string;
  reviewUrlExpiresInSeconds: number;
}

export type VendorOwnershipRequestStatus = 'submitted' | 'needs_information' | 'approved' | 'rejected' | 'cancelled';
export type VendorOwnershipRequestType = 'portal_owner_transfer' | 'legal_ownership_change';

export interface VendorOwnershipRequestItem {
  requestId: string;
  vendorId: string;
  requestType: VendorOwnershipRequestType;
  requestedByVendorUserId: string;
  targetVendorUserId: string | null;
  details: Record<string, unknown>;
  status: VendorOwnershipRequestStatus;
  reviewNote: string | null;
  reviewedBySub: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  cancelledAt: string | null;
  updatedAt: string;
  vendor: {
    businessName: string;
    glpVendorId: string | null;
    status: string;
    ownerVendorUserId: string | null;
  };
  requestedBy: { email: string; givenName: string | null; familyName: string | null; status: string };
  targetUser: { email: string; givenName: string | null; familyName: string | null; status: string } | null;
}

export interface VendorOwnershipListResult {
  status: string;
  count: number;
  items: VendorOwnershipRequestItem[];
  nextCursor: string | null;
}

/** A catalogue product as a reviewer sees it. The barcode is never sent whole. */
export interface ReviewProduct {
  glpProductId: string;
  name: string;
  brand: string | null;
  size: string | null;
  department: string;
  category: string;
  descriptionShort: string | null;
  ingredients: string | null;
  imageUrl: string | null;
  origin: string | null;
  barcodeLast4: string | null;
  barcodeBeside: 'same' | 'different' | 'none';
}

export type VendorProductReviewState = 'pending' | 'returned';

export interface VendorProductSent {
  name: string | null;
  size: string | null;
  department: string | null;
  category: string | null;
  descriptionShort: string | null;
  ingredients: string | null;
  photoUrl: string | null;
  nutritionLabelUrl: string | null;
  barcode: string | null;
  noBarcode: boolean | null;
  vendorSku: string | null;
  priceCents: number | null;
  source: string | null;
  file: string | null;
  addedAt: string | null;
}

export interface VendorProductReviewItem {
  vendorProductId: string;
  vendor: VendorRef;
  kind: 'matched' | 'new';
  reviewStatus: string;
  note: string | null;
  addedAt: string;
  sent: VendorProductSent | null;
  product: ReviewProduct;
}

export interface VendorProductReviewListResult {
  counts: { pending: number; returned: number };
  items: VendorProductReviewItem[];
}

export type VendorQuestionState = 'with_us' | 'proposed' | 'stuck';

export interface VendorQuestionSent {
  name: string | null;
  size: string | null;
  department: string | null;
  category: string | null;
  descriptionShort: string | null;
  ingredients: string | null;
  photoUrl: string | null;
  nutritionLabelUrl: string | null;
  /** The vendor's own entry, as they typed it. */
  barcode: string | null;
  noBarcode: boolean;
  vendorSku: string | null;
  priceCents: number | null;
  source: string;
  file: string | null;
  addedAt: string;
}

export interface VendorQuestionItem {
  questionId: string;
  vendorProductId: string;
  vendor: VendorRef;
  state: VendorQuestionState;
  reason: 'barcode_name_differs' | 'name_similar' | 'staff_proposal' | string;
  askedAt: string;
  handedAt: string | null;
  stuckReason: string | null;
  updatedAt: string;
  sent: VendorQuestionSent | null;
  current: ReviewProduct;
  vendorAnswer: 'yes' | 'no' | null;
  reopened: { barcode: string | null; photoUrl: string | null; note: string | null; at: string }[];
  staff: { action: 'propose' | 'create_new' | 'cant_identify'; by: string; at: string; glpProductId?: string; note?: string }[];
}

export interface VendorQuestionListResult {
  counts: Record<VendorQuestionState, number>;
  items: VendorQuestionItem[];
}

export interface VendorQuestionDecision {
  questionId: string;
  vendorProductId: string;
  glpProductId: string;
  /** null = live · questioned = the vendor's turn · unresolvable = stuck. */
  blockState: string | null;
  assignedBarcode: string | null;
  missing: string[];
}

export type BarcodeAnswer = { kind: 'value'; value: string } | { kind: 'none' };

export type ChangeField =
  | 'name'
  | 'department_category'
  | 'size'
  | 'description_short'
  | 'description_long'
  | 'ingredients'
  | 'nutrition_label'
  | 'photo'
  | 'name_es'
  | 'description_short_es'
  | 'description_long_es'
  | 'ingredients_es'
  | 'other';

export type ChangeRequestState = 'open' | 'approved' | 'rejected';

export interface VendorChangeRequestItem {
  requestId: string;
  vendor: VendorRef;
  vendorProductId: string;
  field: ChangeField;
  currentValue: string | null;
  proposedValue: string | null;
  reason: string | null;
  attachmentUrl: string | null;
  state: ChangeRequestState;
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
  /** The vendor made this product and nobody else sells it. */
  ownProduct: boolean;
  product: ReviewProduct & {
    nameEs: string | null;
    descriptionShortEs: string | null;
    descriptionLong: string | null;
    descriptionLongEs: string | null;
    ingredientsEs: string | null;
    fullImageUrl: string | null;
  };
}

export interface VendorChangeRequestListResult {
  counts: Partial<Record<ChangeRequestState | 'withdrawn', number>>;
  items: VendorChangeRequestItem[];
}

export interface ApproveChangeBody {
  value?: string;
  size?: { sizeValue: number; sizeUnit: string };
  department?: string;
  category?: string;
  note?: string;
}

export interface VendorSettings {
  imageGeneration: { enabled: boolean; updatedAt: string | null };
}

// ─── Manage vendors (post-approval) ──────────────────────────────────────────

export interface VendorAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface VendorCommissionRow {
  scheduleId: string;
  rateBps: number;
  effectiveFrom: string;
  note: string | null;
}

export interface VendorMember {
  membershipId: string;
  role: 'owner' | 'manager' | 'clerk';
  user: { glpUserId: string; email: string; displayName: string | null } | null;
}

export interface ManagedVendorItem {
  vendorId: string;
  glpVendorId: string | null;
  ownerUserId: string;
  businessName: string;
  businessType: string;
  posSystem: string;
  address: VendorAddress | null;
  hours: Record<string, unknown> | null;
  categories: string[] | null;
  snapAuthorized: boolean;
  snapProcessorId: string | null;
  stripeConnectAccountId: string | null;
  status: VendorStatus;
  createdAt: string;
  updatedAt: string;
  /** Latest commission schedule (one row) — drives the list commission column. */
  commissionSchedules: { rateBps: number; effectiveFrom: string }[];
}

export interface ManagedVendorDetail extends Omit<ManagedVendorItem, 'commissionSchedules'> {
  owner: { glpUserId: string; email: string; displayName: string | null } | null;
  memberships: VendorMember[];
  /** Full commission history (newest first). */
  commissionSchedules: VendorCommissionRow[];
}

export type ManagedVendorFilter = VendorStatus | 'all';

export interface ManagedVendorListFilters {
  status?: ManagedVendorFilter;
  q?: string;
  before?: string;
  limit?: number;
}

export interface ManagedVendorListResult {
  status: string;
  count: number;
  items: ManagedVendorItem[];
  nextCursor: string | null;
  counts: Record<string, number>;
}

export interface UpdateManagedVendorInput {
  businessName?: string;
  businessType?: string;
  categories?: string[];
  hours?: Record<string, unknown>;
}

/** Read-only application context returned alongside a vendor detail. */
export interface VendorApplicationContext {
  applicationId: string;
  businessInfo: VendorBusinessInfo;
}

export interface ManagedVendorDetailResult {
  vendor: ManagedVendorDetail;
  application: VendorApplicationContext | null;
}

export type VendorCircularStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'published' | 'expired' | 'withdrawn';
export interface VendorCircularListItem {
  issueId: string;
  vendorId: string;
  publicationDate: string;
  displayStartsOn: string;
  displayEndsOn: string;
  cutoffAt: string;
  businessTimezone: string;
  templateKey: string;
  locationScope: string;
  status: VendorCircularStatus;
  submittedAt: string | null;
  vendor: { businessName: string; glpVendorId: string | null };
  _count: { offers: number; locations: number; pdfs: number };
}
export interface VendorCircularDetail extends VendorCircularListItem {
  offers: Array<{ issueOfferId: string; departmentKey: string; displaySelected: boolean; featured: boolean; offerSnapshot: Record<string, unknown>; products: Array<{ offerProductId: string; glpProductId: string; productNameSnapshot: string; brandSnapshot: string | null; circularImage: { cdnUrl: string | null; status: string } | null; product: { primaryImageUrl: string | null } }> }>;
  locations: Array<{ storeId: string; store: { name: string; formattedAddress: string | null } }>;
  pdfs: Array<{ pdfId: string; storeId: string; locale: string; status: string; cdnUrl: string | null; errorCode: string | null; store: { name: string; formattedAddress: string | null } }>;
  reviews: Array<{ reviewId: string; action: string; reason: string | null; actorSub: string; createdAt: string }>;
}
export interface VendorCircularImageReview {
  imageId: string;
  vendorId: string;
  vendorProductId: string;
  glpProductId: string;
  reviewUrl: string | null;
  status: string;
  rejectionReason: string | null;
  createdAt: string;
  vendor: { businessName: string; glpVendorId: string | null };
  product: { itemName: string; brand: string | null; primaryImageUrl: string | null };
}

export interface VendorDiscountReport {
  reportId: string;
  vendorId: string;
  orderLocationId: string;
  state: string;
  expectedDiscountCents: number;
  appliedDiscountCents: number;
  runnerNote: string | null;
  evidenceBundle: unknown;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  vendor: { businessName: string; glpVendorId: string | null };
  orderLocation: {
    store: { storeId: string; name: string; formattedAddress: string | null };
    order: { orderId: string; glpOrderId: string | null; createdAt: string };
    vendorOrderEvidence: Array<Record<string, unknown>>;
    vendorOrderEvents: Array<Record<string, unknown>>;
    vendorOrderSettlement: Record<string, unknown> | null;
  };
  adjustments: VendorSettlementAdjustment[];
}

export interface VendorSettlementAdjustment {
  adjustmentId: string;
  vendorId: string;
  orderLocationId: string | null;
  discountReportId: string | null;
  reasonCode: string;
  responsibility: string;
  proposedAmountCents: number;
  approvedAmountCents: number | null;
  state: string;
  evidenceBundle: unknown;
  reportedByActorType: string;
  reportedByActorId: string | null;
  reviewedBySub: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  appealedAt: string | null;
  appealText: string | null;
  appealDecision: string | null;
  appealDecidedBySub: string | null;
  appealDecidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  vendor: { businessName: string; glpVendorId: string | null };
  orderLocation: { store: { storeId: string; name: string; formattedAddress: string | null }; order: { orderId: string; glpOrderId: string | null; createdAt: string }; vendorOrderEvidence: Array<Record<string, unknown>>; vendorOrderEvents: Array<Record<string, unknown>> } | null;
  settlement: Record<string, unknown> | null;
  discountReport: Record<string, unknown> | null;
  appliedPayout: { payoutId: string; periodStart: string; periodEnd: string; status: string; paidAt: string | null } | null;
}

function qs(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
}

export const adminApiClient = {
  getCapabilities: () => adminApi.get<MeCapabilities>('/admin/me/capabilities'),

  // ── New vendor products (vendor:read view · vendor:review decide) ──
  listVendorProductReview: (filters: { state?: VendorProductReviewState; vendorId?: string; limit?: number } = {}) =>
    adminApi.get<VendorProductReviewListResult>(`/admin/vendor-products/review${qs({ ...filters })}`),
  approveVendorProduct: (vendorProductId: string) =>
    adminApi.post<unknown>(`/admin/vendor-products/${encodeURIComponent(vendorProductId)}/approve`),
  returnVendorProduct: (vendorProductId: string, note: string) =>
    adminApi.post<unknown>(`/admin/vendor-products/${encodeURIComponent(vendorProductId)}/return`, { note }),

  // ── Vendor applications ──
  listVendorApplications: (filters: VendorAppListFilters = {}) =>
    adminApi.get<VendorAppListResult>(`/admin/vendor-applications${qs({ ...filters })}`),
  getVendorApplication: (id: string) =>
    adminApi.get<{ application: VendorApplicationDetail }>(`/admin/vendor-applications/${id}`),
  transitionVendorApplication: (id: string, body: VendorTransitionBody) =>
    adminApi.post<{ application: VendorApplicationItem }>(
      `/admin/vendor-applications/${id}/transition`,
      body,
    ),
  approveVendorApplication: (id: string, commissionBps?: number) =>
    adminApi.post<{ application: VendorApplicationItem }>(
      `/admin/vendor-applications/${id}/approve`,
      commissionBps != null ? { commissionBps } : {},
    ),
  unlockVendorApplication: (id: string) =>
    adminApi.post<{ application: VendorApplicationItem }>(
      `/admin/vendor-applications/${id}/unlock`,
    ),

  // ── Vendor review queue (vendor:read view · vendor:review decide) ──
  listVendorLocations: (status: VendorLocationStatus | 'all' = 'verifying') =>
    adminApi.get<VendorLocationListResult>(`/admin/vendor-locations${qs({ status, limit: 200 })}`),
  decideVendorLocation: (storeId: string, body: { decision: 'live' | 'needs_changes'; note?: string; fnsVerified?: boolean; fnsSourceUrl?: string }) =>
    adminApi.post<{ location: VendorLocationItem }>(`/admin/vendor-locations/${storeId}/decision`, body),
  separateVendorLocation: (storeId: string) =>
    adminApi.post<{ location: VendorLocationItem }>(`/admin/vendor-locations/${storeId}/separate`),
  listVendorCertifications: (status: 'pending' | 'verified' | 'rejected' | 'expired' | 'all' = 'pending') =>
    adminApi.get<VendorCertificationListResult>(`/admin/vendor-certifications${qs({ status, limit: 200 })}`),
  getVendorCertification: (certificationId: string) =>
    adminApi.get<{ certification: VendorCertificationReviewItem; evidence: VendorCertificationEvidence }>(`/admin/vendor-certifications/${certificationId}`),
  decideVendorCertification: (certificationId: string, body: { decision: 'verified' } | { decision: 'rejected'; reason: string }) =>
    adminApi.post<{ certification: VendorCertificationReviewItem }>(`/admin/vendor-certifications/${certificationId}/decision`, body),
  listVendorOwnershipRequests: (status: 'open' | VendorOwnershipRequestStatus | 'all' = 'open') =>
    adminApi.get<VendorOwnershipListResult>(`/admin/vendor-ownership-requests${qs({ status, limit: 200 })}`),
  getVendorOwnershipRequest: (requestId: string) =>
    adminApi.get<{ request: VendorOwnershipRequestItem }>(`/admin/vendor-ownership-requests/${requestId}`),
  decideVendorOwnershipRequest: (requestId: string, body: { decision: 'approved' } | { decision: 'rejected' | 'needs_information'; note: string }) =>
    adminApi.post<{ request: VendorOwnershipRequestItem }>(`/admin/vendor-ownership-requests/${requestId}/decision`, body),
  listVendorQuestions: (state: VendorQuestionState | 'all' = 'with_us') =>
    adminApi.get<VendorQuestionListResult>(`/admin/vendor-questions${qs({ state, limit: 200 })}`),
  searchReviewProducts: (q: string) =>
    adminApi.get<{ items: ReviewProduct[] }>(`/admin/vendor-review/products${qs({ q })}`),
  proposeVendorMatch: (questionId: string, body: { glpProductId: string; note?: string }) =>
    adminApi.post<VendorQuestionDecision>(`/admin/vendor-questions/${questionId}/propose`, body),
  createVendorOwnProduct: (questionId: string, barcode: BarcodeAnswer) =>
    adminApi.post<VendorQuestionDecision>(`/admin/vendor-questions/${questionId}/create-new`, { barcode }),
  cantIdentifyVendorProduct: (questionId: string, reason: string) =>
    adminApi.post<VendorQuestionDecision>(`/admin/vendor-questions/${questionId}/cant-identify`, { reason }),
  listVendorChangeRequests: (state: ChangeRequestState | 'all' = 'open') =>
    adminApi.get<VendorChangeRequestListResult>(`/admin/vendor-change-requests${qs({ state, limit: 200 })}`),
  approveVendorChangeRequest: (requestId: string, body: ApproveChangeBody) =>
    adminApi.post<{ request: VendorChangeRequestItem }>(`/admin/vendor-change-requests/${requestId}/approve`, body),
  rejectVendorChangeRequest: (requestId: string, note: string) =>
    adminApi.post<{ request: VendorChangeRequestItem }>(`/admin/vendor-change-requests/${requestId}/reject`, { note }),
  getVendorSettings: () => adminApi.get<VendorSettings>('/admin/vendor-settings'),
  setVendorImageGeneration: (enabled: boolean) =>
    adminApi.put<VendorSettings>('/admin/vendor-settings/image-generation', { enabled }),
  listVendorCirculars: (status: VendorCircularStatus | 'all' = 'submitted') =>
    adminApi.get<{ items: VendorCircularListItem[]; counts: Record<string, number> }>(`/admin/vendor-circulars${qs({ status, limit: 200 })}`),
  getVendorCircular: (issueId: string) => adminApi.get<{ issue: VendorCircularDetail }>(`/admin/vendor-circulars/${issueId}`),
  decideVendorCircular: (issueId: string, decision: 'approve' | 'reject', reason?: string) =>
    adminApi.post<{ issue: VendorCircularListItem }>(`/admin/vendor-circulars/${issueId}/decision`, { decision, ...(reason ? { reason } : {}) }),
  withdrawVendorCircular: (issueId: string, reason: string) =>
    adminApi.post<{ issue: VendorCircularListItem }>(`/admin/vendor-circulars/${issueId}/withdraw`, { reason }),
  listVendorCircularImages: (status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending') =>
    adminApi.get<{ items: VendorCircularImageReview[]; counts: Record<string, number> }>(`/admin/vendor-circular-images${qs({ status, limit: 200 })}`),
  decideVendorCircularImage: (imageId: string, decision: 'approve' | 'reject', reason?: string) =>
    adminApi.post(`/admin/vendor-circular-images/${imageId}/decision`, { decision, ...(reason ? { reason } : {}) }),

  // ── Vendor settlement exceptions (vendor:read view · vendor:review decide) ──
  listVendorDiscountReports: (state = 'reported') =>
    adminApi.get<{ items: VendorDiscountReport[]; counts: Record<string, number> }>(`/admin/vendor-discount-reports${qs({ state, limit: 200 })}`),
  getVendorDiscountReport: (reportId: string) =>
    adminApi.get<{ report: VendorDiscountReport }>(`/admin/vendor-discount-reports/${reportId}`),
  decideVendorDiscountReport: (reportId: string, body: { decision: 'confirm'; note: string; receiptConfirmed: true; transactionConfirmed: true; agreementConfirmed: true; orderRelationshipConfirmed: true; appliedDiscountCents?: number } | { decision: 'request_more_evidence' | 'dismiss'; note: string }) =>
    adminApi.post(`/admin/vendor-discount-reports/${reportId}/decision`, body),
  listVendorSettlementAdjustments: (state = 'reported') =>
    adminApi.get<{ items: VendorSettlementAdjustment[]; counts: Record<string, number> }>(`/admin/vendor-settlement-adjustments${qs({ state, limit: 200 })}`),
  getVendorSettlementAdjustment: (adjustmentId: string) =>
    adminApi.get<{ adjustment: VendorSettlementAdjustment }>(`/admin/vendor-settlement-adjustments/${adjustmentId}`),
  decideVendorSettlementAdjustment: (adjustmentId: string, body: { decision: 'approve'; approvedAmountCents: number; note: string } | { decision: 'reject' | 'request_more_evidence'; note: string }) =>
    adminApi.post(`/admin/vendor-settlement-adjustments/${adjustmentId}/decision`, body),
  decideVendorSettlementAppeal: (adjustmentId: string, decision: 'uphold' | 'reverse', reason: string) =>
    adminApi.post(`/admin/vendor-settlement-adjustments/${adjustmentId}/appeal-decision`, { decision, reason }),

  // ── Manage vendors (vendor:read view · vendor:manage write) ──
  listVendors: (filters: ManagedVendorListFilters = {}) =>
    adminApi.get<ManagedVendorListResult>(`/admin/vendors${qs({ ...filters })}`),
  getVendor: (id: string) => adminApi.get<ManagedVendorDetailResult>(`/admin/vendors/${id}`),
  updateVendor: (id: string, input: UpdateManagedVendorInput) =>
    adminApi.patch<{ vendor: ManagedVendorItem }>(`/admin/vendors/${id}`, input),
  suspendVendor: (id: string, reason?: string) =>
    adminApi.post<{ vendor: ManagedVendorItem }>(
      `/admin/vendors/${id}/suspend`,
      reason ? { reason } : {},
    ),
  reactivateVendor: (id: string) =>
    adminApi.post<{ vendor: ManagedVendorItem }>(`/admin/vendors/${id}/reactivate`),
};
